<template>
  <div>
    <div class="row">
      <div class="col-6 d-flex align-items-center justify-content-lg-start">
        <Button
          v-if="showBackButton && actionType === 'save' || 'add'"
          :label="$t('List page')"
          size="md"
          icon="ArrowLeftIcon"
          show-icon
          class="mb-1"
          @click="backNavigationAction"
        />
      </div>
      <div class="col-6 d-flex justify-content-end">
        <Button
          v-if="showSearchButton"
          :label="$t('Search')"
          size="md"
          icon="SearchIcon"
          show-icon
          @click="showSearchField"
        />
      </div>
    </div>
    <b-card v-if="!showSearchButton">
      <b-row class="position-relative">
        <b-col md="4">
          <validation-observer ref="searchRules">
            <b-form @submit.prevent="handleSearch">
              <b-row>
                <Alert
                  :show="showFormErrorAlert"
                  :message="$t('Customer\'s account name or account number or customer id is required')"
                />

                <b-col
                  md="12"
                  class="mt-1"
                >
                  <InputField
                    id="customer-id"
                    v-model="searchParams.customerID"
                    :label="$t('Customer ID')"
                    :placeholder="$t('Enter customer id')"
                    type="text"
                    for="customer-id"
                  />
                </b-col>
                <b-col md="12">
                  <InputField
                    id="customer-name"
                    v-model="searchParams.name"
                    :label="$t('Customer\'s Name')"
                    :placeholder="$t('Enter customer\'s name')"
                    type="text"
                    error-name=""
                    rules=""
                  />
                </b-col>
                <b-col md="12">
                  <InputField
                    id="email"
                    v-model="searchParams.email"
                    :label="$t('Email')"
                    :placeholder="$t('Enter email')"
                    type="text"
                    error-name=""
                    rules=""
                  />
                </b-col>

                <b-col md="12">
                  <Button
                    :label="$t('Search')"
                    size="md"
                    class="mt-2"
                    block
                    @click="handleSearch"
                  />
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
    </b-card>

    <form-wizard
      v-if="showSearchButton"
      ref="fDAccountForm"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :back-button-text="$t('Previous')"
      :next-button-text="$t('Next')"
      class="wizard-vertical mb-3"
    >

      <!-- Customer Information -->
      <tab-content
        :title="$t('Customer Information')"
        :before-change="validateLiquidationAccountInfo"
      >
        <validation-observer ref="customerDetailsRule">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-3 mt-2"
              >
                <h5 class="section-label mb-0 mt-0">
                  {{ $t('Customer Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <InputField
                  id="customer-id"
                  v-model="customer.customerID"
                  :label="$t('Customer ID')"
                  :placeholder="$t('Enter customer id')"
                  type="text"
                  for="customer-id"
                  readonly
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="customer-name"
                  v-model="customer.customerName"
                  :label="$t('Customer Name')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="fd-account-name"
                  v-model="customer.fdAccountName"
                  :label="$t('FD Account Name')"
                  type="text"
                  :error-name="$t('FD Account Name')"
                  rules="required"
                  :readonly="isEditable"
                  onpaste="return false;"
                  :is-allow-special-characters="false"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

        <validation-observer ref="liquidationAccountRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="section-label mb-0 mt-3">
                  {{ $t('Liquidation Account Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <h6 class="mt-0 mb-0">
                  {{ $t('Account Type') }}
                </h6>
                <b-form-radio-group
                  v-model="fixedDepositFormData.liquidationAccountType"
                  :options="translatedLiquidationAccountTypeOptions"
                  class="demo-inline-spacing"
                  name="radios-stacked"
                  stacked
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                  :disabled="isCustomDisabled"
                  @change="
                    handleAccountType(fixedDepositFormData.liquidationAccountType)
                  "
                />
              </b-col>
              <b-col md="6">
                <!--  Customer's Savings/Current Account -->
                <b-col
                  v-if="fixedDepositFormData.liquidationAccountType === 'SavingsAndCurrent'"
                  md="12"
                >
                  <SelectField
                    id="saving-account"
                    v-model="customers.linkedNumber"
                    :label="$t('Customer\'s Savings/Current Account')"
                    :options="customerAccountOptions"
                    :placeholder="$t('Select')"
                    :reduce="option => option.value"
                    item="label"
                    :set-selected="setCustomerDetails"
                    :readonly="isEditable"
                  />
                </b-col>
                <!--  Customer's Savings/Current Account -->
              </b-col>
            </b-row>
            <b-row v-if="fixedDepositFormData.liquidationAccountType === 'GeneralLedgerAccount'">
              <b-col md="6">
                <InputField
                  id="account-number"
                  v-model="fixedDepositFormData.accountNumber"
                  :label="$t('GL Number')"
                  type="text"
                  :placeholder="$t('Enter gl number')"
                  :error-name="$t('GL Number')"
                  rules="required|integer"
                  :readonly="isEditable"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="account-name"
                  v-model="fixedDepositFormData.accountName"
                  :label="$t('GL Name')"
                  type="text"
                  :placeholder="$t('Enter gl name')"
                  :error-name="$t('GL Name')"
                  rules="required"
                  :readonly="isEditable"
                />
              </b-col>
              <b-col md="6">
                <div class="wizard-footer-right">
                  <Button
                    :label="$t('Search')"
                    :loading="isLoading"
                    :disabled="isCustomDisabled"
                    size="sm"
                    @click="onSearchGlAccount"
                  />
                </div>
              </b-col>
            </b-row>
          </b-form>
          <b-row
            v-if="fixedDepositFormData.liquidationAccountType === 'SavingsAndCurrent'"
            class="mt-3"
          >
            <b-col md="6">
              <InputField
                id="case-name"
                v-model="fixedDepositFormData.casaName"
                :label="$t('CASA Name')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="case-number"
                v-model="fixedDepositFormData.casaNumber"
                :label="$t('CASA Number')"
                type="number"
                readonly
              />
            </b-col>
          </b-row>
          <b-row
            v-if="fixedDepositFormData.liquidationAccountType === 'GeneralLedgerAccount'"
            class="mt-3"
          >
            <b-col md="6">
              <InputField
                id="case-name"
                v-model="fixedDepositFormData.glName"
                :label="$t('GL Name')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="case-number"
                v-model="fixedDepositFormData.glNumber"
                :label="$t('GL Number')"
                type="number"
                readonly
              />
            </b-col>
          </b-row>
          <b-row v-if="fixedDepositFormData.liquidationAccountType === 'SavingsAndCurrent'">
            <b-col md="6">
              <InputField
                id="account-officer"
                v-model="fixedDepositFormData.accountOfficerName"
                :label="$t('Account Officer')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="account-status"
                v-model="fixedDepositFormData.accountStatus"
                :label="$t('Account Status')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col
              v-if="fixedDepositFormData.liquidationAccountType === 'SavingsAndCurrent'"
              md="6"
            >
              <InputField
                id="branch"
                v-model="fixedDepositFormData.branchName"
                :label="$t('Branch')"
                type="text"
                readonly
              />
            </b-col>
            <b-col
              v-if="fixedDepositFormData.liquidationAccountType === 'GeneralLedgerAccount'"
              md="6"
            >
              <SelectField
                id="branch-name"
                v-model="fixedDepositFormData.branchCode"
                :label="$t('Branch')"
                :reduce="(option) => option.value"
                :options="branchOptions"
                :placeholder="$t('Select branch')"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :set-selected="setBranchInfo"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="product-type"
                v-model="fixedDepositFormData.productCode"
                :label="$t('Product Type')"
                :reduce="option => option.value"
                :options="productTypeOptions"
                :placeholder="$t('Select')"
                :error-name="$t('Product Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setProductTypeValues"
              />
            </b-col>
            <!--  Reference Number -->
            <b-col md="6">
              <InputField
                id="reference number"
                v-model="fixedDepositFormData.referenceNumber"
                :label="$t('Reference Number')"
                :placeholder="$t('Enter reference number')"
                type="text"
                show-icon
                :on-click="onViewInfo"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Currency Code -->
            <b-col md="6">
              <InputField
                id="currency-code"
                v-model="fixedDepositFormData.currencyCode"
                :label="$t('Currency')"
                :placeholder="$t('Enter currency code')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>

          <h5 class="section-label mb-0 mt-3">
            {{ $t('Notification Preference') }}
          </h5>

          <b-row>
            <b-col md="2">
              <h5 class="mt-2 mb-2">
                {{ $t('Notify Via') }}
              </h5>

            </b-col>
            <b-col
              md="2"
              class="mt-2 mb-2"
            >
              <SwitchButton
                v-model="customers.enableEmailNotification"
                :label="$t(' E-MAIL')"
                name="e-mail"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              md="2"
              class="mt-2 mb-2"
            >
              <SwitchButton
                v-model="customers.enableSMSNotification"
                :label="$t('SMS')"
                name="sms"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>
        </validation-observer>

        <div>
          <h6 class="section-label mt-1">
            {{ $t('Signatory Mandate') }}
          </h6>

          <b-row>
            <b-col
              cols="12"
              class="mb-2 mt-2"
            >
              <Button
                :label="$t('View Signatory Mandate')"
                size="sm"
                class="mb-0"
                @click="onViewSignatoryMandate"
              />
            </b-col>
          </b-row>
        </div>

        <b-row
          v-if="customerType.toLowerCase() !== 'individual'"
          class="mt-2 "
        >
          <b-col md="12">
            <h6 class="section-label mb-2 mt-2">
              {{ $t('Signatory Information') }}
            </h6>

            <b-form
              v-if="!isVisible"
              @submit.prevent="createSignatoryAction"
            >
              <validation-observer ref="signatoryRules">
                <b-row>
                  <b-col md="5">
                    <SelectField
                      id="signatory"
                      v-model="signatory"
                      :label="$t('Signatory')"
                      :rules="signatoryRule"
                      :error-name="$t('Signatory')"
                      :reduce="option => option"
                      :options="signatoryOptions"
                      item="label"
                      :placeholder="$t('Select')"
                    />
                  </b-col>
                  <b-col md="5">
                    <SelectField
                      id="category"
                      v-model="category"
                      :label="$t('Category')"
                      rules="required"
                      :error-name="$t('Category')"
                      :reduce="option => option"
                      :options="categoryOptions"
                      item="label"
                      :placeholder="$t('Select')"
                    />
                  </b-col>
                  <b-col
                    md="2"
                    class="mt-2"
                  >
                    <Button
                      :label="$t('Add')"
                      size="md"
                      @click="createSignatoryAction"
                    />
                  </b-col>
                </b-row>
              </validation-observer>
            </b-form>
            <div
              v-if="customerType.toLowerCase() !== 'individual' && signatoryList.length"
              class="mt-1"
            >
              <b-table
                ref="signatoryListTable"
                select-mode="single"
                :items="signatoryList"
                responsive
                :fields="translatedSignatoryListHeaders"
                primary-key="id"
                selectable
                show-empty
                :empty-text="$t('No signatory added')"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                class="position-relative"
              >
                <template #cell(SN)="data">
                  {{ data.index + 1 }}
                </template>
                <template #cell(actions)="data">
                  <Button
                    :label="$t('Remove')"
                    :disabled="isDisabled"
                    size="sm"
                    @click="removeSignatoryAction(data.item)"
                  />
                </template>
              </b-table>
            </div>

          </b-col>
        </b-row>

        <b-row
          v-if="mandate.ruleConfig[0].categoryConfig.length"
          class=""
        >
          <b-col md="12">
            <h6 class="section-label mb-1 mt-0">
              {{ $t('Rule Configuration') }}
            </h6>

            <b-form @submit.prevent="createRuleInfoAction">
              <validation-observer ref="ruleConfigurationRules">
                <b-row
                  v-for="(item) in mandate.ruleConfig[0].categoryConfig"
                  :id="item.category"
                  :key="item.category"
                >
                  <b-col md="6">
                    <InputField
                      id="category"
                      v-model="item.category"
                      :label="$t('Category')"
                      type="text"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputField
                      id="customer-count"
                      v-model.number="item.authorizableNumber"
                      :label="$t('Customer Count')"
                      rules="required|integer|positive"
                      :placeholder="$t('Enter customer count')"
                      type="number"
                    />
                  </b-col>
                </b-row>

                <b-row v-if="mandate.ruleConfig[0].categoryConfig.length">
                  <b-col md="6">
                    <SelectField
                      id="limit"
                      v-model="mandate.ruleConfig[0].isLimited"
                      :label="$t('Limit')"
                      rules="required"
                      :error-name="$t('Limit')"
                      :reduce="option => option.value"
                      :options="translatedLimitOptions"
                      item="label"
                      :placeholder="$t('Select')"
                      :set-selected="setAmountLimit"
                    />
                  </b-col>

                  <b-col
                    v-if="setAmountLimit(mandate.ruleConfig[0].isLimited)"
                    md="6"
                  >
                    <InputNumberField
                      id="amount"
                      v-model.number="mandate.ruleConfig[0].amount"
                      :label="$t('Amount')"
                      :placeholder="$t('Enter amount')"
                      rules="required|integer|positive"
                      :error-name="$t('Amount')"
                    />
                  </b-col>
                </b-row>

                <div class="mt-2 mb-2">
                  <Button
                    v-if="mandate.ruleConfig[0].categoryConfig.length"
                    :disabled="isAddRuleDisabled"
                    :loading="isAddRuleLoading"
                    :label="$t('Add Rule')"
                    size="md"
                    @click="createRuleInfoAction"
                  />
                </div>
              </validation-observer>
            </b-form>
          </b-col>
        </b-row>

        <b-row
          v-if="mandateList.length"
          class=""
        >
          <b-col md="12">
            <h6 class="section-label mb-2 mt-2">
              {{ $t('Mandate Information') }}
            </h6>
            <div>
              <b-table
                ref="mandateListTable"
                select-mode="single"
                :items="mandateList"
                responsive
                :fields="translatedMandateListHeaders"
                primary-key="id"
                selectable
                show-empty
                :empty-text="$t('No mandate added')"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                class="position-relative"
              >
                <template #cell(SN)="data">
                  {{ data.index + 1 }}
                </template>
                <template #cell(isLimited)="data">
                  {{ data.value === false ? 'Unlimited' : 'Limited' }}
                </template>
                <template #cell(amount)="data">
                  {{ data.value === 0 ? 'Unlimited' : toAmount(data.value) }}
                </template>
                <template #cell(actions)="data">
                  <Button
                    :label="$t('Remove')"
                    size="sm"
                    @click="removeMandateAction(data.item)"
                  />
                </template>
              </b-table>

            </div>

          </b-col>
        </b-row>
      </tab-content>

      <!--Mandate Information -->
      <!-- <tab-content
        v-if="fixedDepositFormData.liquidationAccountType === 'SavingsAndCurrent'"
        title="Mandate Information"
        :before-change="validateMandateInformation"
      >

      </tab-content> -->

      <!--Fixed Deposit Details -->
      <tab-content
        :title="$t('Fixed Deposit Details')"
        :before-change="validateFixedDepositInfo"
      >
        <validation-observer ref="fixedDepositDetailsRule">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-0"
              >
                <h5 class="section-label mb-0">
                  {{ $t('Fixed Deposit Details') }}
                </h5>
              </b-col>
            </b-row>
            <b-row class="mt-3">
              <b-col md="6">
                <ValidationObserver>
                  <InputNumberField
                    id="principal-amount"
                    v-model="fixedDepositFormData.principalAmount"
                    :label="$t('Principal Amount')"
                    :placeholder="$t('Principal Amount')"
                    :error-name="$t('Principal Amount')"
                    rules="required"
                    :readonly="isEditable"
                  />
                </ValidationObserver>
              </b-col>
              <b-col md="6">
                <InputNumberField
                  id="duration"
                  v-model.number="fixedDepositFormData.duration"
                  rules="required|positive"
                  :label="$t('Duration (days)')"
                  :error-name="$t('Duration')"
                  :placeholder="$t('Duration (days)')"
                  :readonly="isEditable"
                  :is-allow-negative-numbers="false"
                  onpaste="return false;"
                />

              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <DatePickerField
                  id="start-date"
                  v-model="fixedDepositFormData.commencementDate"
                  rules="required"
                  :label="$t('Commencement Date')"
                  :error-name="$t('Commencement Date')"
                  :on-change="setEndDate"
                  :config="datePickerConfig"
                  :placeholder="$t('Select Commencement Date')"
                  :disabled="isCustomDisabled"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="interest-accrual"
                  v-model="fixedDepositFormData.maturityDate"
                  :label="$t('Maturity Date')"
                  type="text"
                  :error-name="$t('Maturity Date')"
                  rules="required"
                  readonly
                />
              </b-col>
            </b-row>
            <b-row>
              <!-- Interest Rate-->
              <b-col md="6">
                <SelectField
                  id="applicable-interest"
                  v-model="fixedDepositFormData.interestID"
                  :label="$t('Applicable Interest')"
                  :reduce="option => option.value"
                  :options="interestOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Applicable Interest')"
                  rules="required"
                  item="label"
                  :set-selected="setProductCreditInterestFee"
                  :readonly="isCustomDisabled"
                />
              </b-col>
              <!-- Interest Rate-->
              <b-col md="6">
                <InputNumberField
                  id="interest-rate"
                  v-model.number="fixedDepositFormDatas.interestRate"
                  :label="$t('Interest Rate (%)')"
                  type="number"
                  :error-name="$t('Interest Rate (%)')"
                  rules="required|positive|max-value:100"
                  :readonly="isCustomDisabled"
                  :is-allow-negative-numbers="false"
                  onpaste="return false;"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <SwitchButton
                  v-model="fixedDepositFormData.applyInterestPayoutFrequency"
                  :label="$t('Apply Interest Payment Frequency')"
                  name="apply-interest-payment-frequency"
                  :readonly="isReadonly"
                  :change="setInterestPayoutFrequency(fixedDepositFormData.applyInterestPayoutFrequency)"
                />
              </b-col>
              <b-col
                v-if="isInterestPaymentFrequency"
                md="6"
              >
                <SelectField
                  id="interest-payment-frequency"
                  v-model="fixedDepositFormData.interestPayoutFrequency"
                  :label="$t('Interest Payment Frequency')"
                  :reduce="option => option.value"
                  :options="interestPayoutFrequencyOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Interest Payment Frequency')"
                  rules="required"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <InputNumberField
                  id="early-liquidation-Charge"
                  v-model.number="fixedDepositFormData.earlyLiquidationCharge"
                  :label="$t('Early liquidation Charge (%)')"
                  type="number"
                  :error-name="$t('Early liquidation Charge (%)')"
                  rules="required|positive|max-value:100"
                  :readonly="isReadonly"
                  :is-allow-negative-numbers="false"
                  onpaste="return false;"
                />
              </b-col>
            </b-row>
          </b-form>
          <b-row class="mb-3 mt-2">
            <b-col md="6">
              <SwitchButton
                v-model="customers.withholdFundAtMaturity"
                :label="$t('Withhold Funds at Maturity')"
                name="withold-fund-maturity"
                :readonly="isReadonly"
              />
              <SwitchButton
                v-model="customers.applyRollover"
                :label="$t('Apply Rollover')"
                name="apply-rollover"
                :readonly="isReadonly"
                :checked="true"
                :change="setApplyRollover(customers.applyRollover)"
              />
            </b-col>
          </b-row>
          <b-row v-if="isApplyRollover">
            <b-col
              cols="12"
              class="mb-1"
            >
              <h5 class="section-label mb-0">
                {{ $t('Rollover Information') }}
              </h5>
            </b-col>
            <b-col
              md="6"
              class="mb-3"
            >
              <b-form-radio-group
                v-model="customers.rolloverType"
                :options="translatedRolloverTypeOptions"
                class="demo-inline-spacing"
                name="radios-stacked"
                stacked
                value-field="value"
                text-field="text"
                :disabled="isDisabled"
              />
            </b-col>
          </b-row>
          <b-row v-if="actionType === 'add'">
            <b-col
              cols="12"
              class="mb-2 mt-2"
            >
              <h5 class="mb-2 section-label">
                {{ $t('View Investment Details') }}
              </h5>

              <Button
                :label="$t('View Investment Details')"
                size="sm"
                class="mb-2"
                @click="computeInterest"
              />
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>

      <template slot="finish">
        <div class="wizard-footer-right">
          <Button
            :label="$t('Submit')"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="handleSubmit"
          />
        </div>

      </template>

      <FullScreenSpinner :show="showSpinner" />
    </form-wizard>

    <b-modal
      id="search-modal"
      ref="search-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Account Details')"
    >
      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        :per-page="pageSize"
        :current-page="currentPage"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectCustomer"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template #modal-footer="{ }">
        <div />
      </template>
    </b-modal>

    <b-modal
      id="signatory-mandate-table"
      ref="signatoryMandateTable"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Signatory Mandate')"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >

            <!-- <TableEntries
              v-model="perPage"
              :options="perPageOptions"
            /> -->

          </b-col>

        </b-row>

      </div>

      <b-table
        ref="signatoryMandateTable"
        select-mode="single"
        :items="signatoryMandateList"
        responsive
        :fields="translatedSignatoryMandateTableHeaders"
        :per-page="perPage"
        :current-page="currentPage"
        primary-key="id"
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative mt-2"
      >
        <template #cell(SN)="data">
          {{ data.index + 1 }}
        </template>

        <template #cell(passport)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.passport"
                button
                @click="onViewPassport(data.item.passport, data.item.fullName)"
              />
            </template>
          </b-media>
        </template>

        <template #cell(signature)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.signature"
                button
                @click="onViewSignature(data.item.signature, data.item.fullName)"
              />
            </template>
          </b-media>
        </template>
      </b-table>

      <!-- <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      /> -->

      <template #modal-footer="{ }">
        <div />
      </template>
    </b-modal>
    <b-modal
      id="avatar-modal"
      ref="avatarModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="avatarModalTitle"
      @hidden="resetAvatarModal"
      @cancel="resetAvatarModal"
    >

      <div class="m-2 text-center">

        <b-row>
          <b-col cols="12">

            <b-avatar
              size="30rem"
              :src="avatarLg"
            />
          </b-col>

        </b-row>

      </div>

      <template #modal-footer="{ }">
        <div />
      </template>
    </b-modal>

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-modal')"
    >
      <div class="p-2">
        <p>{{ $t('External Fixed Deposit Account Reference') }} </p>
      </div>
    </b-modal>
    <b-modal
      id="info-investment-modal"
      ref="infoInvestmentModal"
      centered
      :title="$t('View Investment Details')"
      size="md"
      ok-only
      ok-title="Okay"
      @ok="hideModal('info-investment-modal')"
    >
      <div class="container">
        <div class="column-12 mt-3">
          <table>
            <tbody>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Investment Amount') }} :
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.investmentAmount }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Tenor') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.tenure }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Interest Rate') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.applicableInterestRate }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Effective Date') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.effectiveDate }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Maturity Date') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.maturityDate }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Gross Interest Amount') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.grossInterestAmount }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Net Payout') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.netPayout }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Liquidation Account') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.liquidationAccount }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: 14px; margin-top: -10px;">
                    {{ $t('Investment Product') }}:
                  </p>
                </td>
                <td>
                  <p style="margin-top: -10px; text-align: right;">
                    {{ computeInterestData.productName }}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </div>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import {
  BRow,
  BCol,
  BMedia,
  BAvatar,
  BForm,
  BCard,
  VBTooltip,
  BTable,
  BFormRadioGroup,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapActions, mapGetters } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import { required, maxValue, positive } from '@validations'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
// import TableEntries from '@/views/components/table/TableEntries.vue'
import { transformMandateRule, transformMandateRuleConfig } from '@/helpers/transformers'
import { toAmount, toBalance, toKobo } from '@/helpers/formatAmount'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import SwitchButton from '../../../../components/switch/Switch.vue'
import getStatusColor from '../../../../../helpers/getStatusColor'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { category } from '../../../../../helpers/data'
import Button from '../../../../components/button/Button.vue'
import Alert from '../../../../components/alert/Alert.vue'
import getEndDate from '../../../../../helpers/getEndDate'
import { toYearWithDate, toDateWithYear } from '../../../../../helpers/formatDate'
import { accountPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    FormWizard,
    TabContent,
    BCard,
    BRow,
    BForm,
    BCol,
    Button,
    BTable,
    FullScreenSpinner,
    SwitchButton,
    InputField,
    SelectField,
    ValidationObserver,
    BMedia,
    BAvatar,
    Alert,
    TablePagination,
    DatePickerField,
    BFormRadioGroup,
    InputNumberField,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert],
  data() {
    return {
      customerType: '',
      required,
      toAmount,
      maxValue,
      positive,
      isDisabled: false,
      isCustomDisabled: false,
      isVisible: '',
      showEditable: '',
      isInterestPaymentFrequency: false,
      isLoading: false,
      isEditable: false,
      isReadonly: false,
      showSpinner: false,
      showSearchButton: false,
      showBackButton: false,
      showCustomerInfo: false,
      showFormErrorAlert: false,
      sortBy: 'accountName',
      customerList: [],
      actionType: 'add',
      searchParams: {
        customerID: '',
        name: '',
        email: '',
      },
      fixedDepositSchedulePagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      customerSearchPagination: {
        currentPage: 1,
        from: this.customerSearchFrom,
        to: this.customerSearchTo,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },

      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'customerName', sortable: true, label: 'Customer Name' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
      ],

      signatoryMandateTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'passport', sortable: true, label: 'Passport' },
        { key: 'signature', sortable: true, label: 'Signature' },
      ],
      getStatusColor,
      avatarLg: '',
      avatarModalTitle: '',

      // Started Here
      isAccountType: true,
      isApplyRollover: true,
      isAddRuleDisabled: false,
      isAddRuleLoading: false,
      signatoryRule: 'required',
      customers: {
        linkedNumber: '',
        enableEmailNotification: false,
        enableSMSNotification: false,
        withholdFundAtMaturity: false,
        applyRollover: true,
        rolloverType: null,
        mandateRuleConfig: {
          ruleConfig: [],
        },
      },
      customer: {
        customerID: '',
        customerName: '',
        fdAccountName: '',
      },
      fixedDepositFormDatas: {
        interestRate: '',
      },
      fixedDepositFormData: {
        liquidationAccountType: 'SavingsAndCurrent',
        accountNumber: '',
        accountName: '',
        casaName: '',
        casaNumber: '',
        glName: '',
        glNumber: '',
        accountOfficerName: '',
        accountStatus: '',
        branchName: '',
        branchCode: '',
        productCode: '',
        productName: '',
        accountCreationChannel: '',
        principalAmount: '',
        duration: '',
        commencementDate: '',
        maturityDate: '',
        interestID: '',
        interestCode: '',
        earlyLiquidationCharge: '',
        signatories: '',
        id: '',
        referenceNumber: '',
        applyInterestPayoutFrequency: false,
        interestPayoutFrequency: '',
        currencyCode: '',
      },

      liquidationAccountTypeOptions: [
        { text: 'Savings/Current', value: 'SavingsAndCurrent', disabled: false },
        { text: 'General Ledger', value: 'GeneralLedgerAccount', disabled: false },
      ],
      rolloverTypeOptions: [
        { text: 'Rollover Principal Only', value: 'RolloverPrincipalOnly' },
        { text: 'Rollover Principal + Interest', value: 'RolloverPrincipalAndInterest' },
      ],
      notificationPreferenceOptions: [
        { text: 'E-Mail', value: 'Email' },
        { text: 'SMS', value: 'SMS' },
      ],
      productTypeOptions: [],
      limitOptions: [{ label: 'Unlimited', value: false }, { label: 'Limited', value: true }],
      categoryOptions: category,
      selectedSignatories: [],
      signatory: '',
      category: '',
      signatoryList: [],
      signatories: [],
      signatoryListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'signatory', sortable: true, label: 'Signatory' },
        { key: 'category', sortable: true, label: 'Category' },
        { key: 'actions', label: 'Actions' },
      ],
      mandateListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'rule', sortable: true, label: 'Rule' },
        { key: 'isLimited', sortable: true, label: 'Limit' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'actions', label: 'Actions' },
      ],
      signatoryOptions: [],
      groupMembers: [],
      mandateInformationFormData: {
        signatory: '',
        category: '',
      },
      mandateList: [],
      signatorySummaryTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'Signatory', sortable: true },
      ],
      signatoryMandateList: [],
      signatorySummaryPagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        defaultDate: '',
        ariaDateFormat: 'd/m/Y',
      },
      computeInterestData: {},
      customerAccountOptions: [],
      interestDetails: [],
      interestOptions: [],
      branchOptions: [],
      mandate: {
        ruleConfig: [
          {
            categoryConfig: [],
            isLimited: true,
            amount: 0,
          },
        ],
      },
      totalSignatoriesCount: 0,
      signatoryCurrentPage: 1,
      signatoriesPerPage: 10,
      permission: '',
      interestPayoutFrequencyOptions: [
        { label: 'Upfront', value: 'Upfront' },
        { label: 'Monthly', value: 'Monthly' },
        { label: 'Quarterly', value: 'Quarterly' },
        { label: 'Half Yearly', value: 'HalfYearly' },
      ],
    }
  },
  computed: {
    totalSignatories() {
      return this.totalSignatoriesCount
    },
    numberOfSignatories() {
      return Math.floor((this.totalSignatoriesCount + this.signatoriesPerPage - 1) / this.perPage)
    },
    signatoriesFrom() {
      return (this.signatoryCurrentPage * this.signatoriesPerPage) - (this.signatoriesPerPage - 1)
    },
    signatoriesTo() {
      return Math.min(this.signatoriesFrom + this.signatoriesPerPage - 1, this.totalSignatoriesCount)
    },
    translatedLiquidationAccountTypeOptions() {
      return this.liquidationAccountTypeOptions.map(header => ({ ...header, text: header.text ? this.$t(header.text) : header.value }))
    },
    translatedRolloverTypeOptions() {
      return this.rolloverTypeOptions.map(header => ({ ...header, text: header.text ? this.$t(header.text) : header.value }))
    },
    translatedNotificationPreferenceOptions() {
      return this.notificationPreferenceOptions.map(header => ({ ...header, text: header.text ? this.$t(header.text) : header.value }))
    },
    translatedLimitOptions() {
      return this.limitOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedSignatoryListHeaders() {
      return this.signatoryListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedMandateListHeaders() {
      return this.mandateListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSignatoryMandateTableHeaders() {
      return this.signatoryMandateTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
  },
  watch: {
    fixedDepositScheduleCurrentPage(newValue) {
      this.fixedDepositSchedulePagination.currentPage = newValue
    },
    fixedDepositSchedulePerPage(newValue) {
      this.fixedDepositSchedulePagination.perPage = newValue
    },
  },
  mounted() {
    const { type } = this.$route.params
    this.actionType = type
    this.getPermission()
    this.fetchFDProducts()
    this.fetchInterest()
    this.getFDAccountDetails()
    this.fetchBranches()
  },
  methods: {
    ...mapActions({
      getCustomer: 'account/getCustomerList',
      getFDProduct: 'account/getProducts',
      customerAccount: 'account/getCustomerAccountList',
      glAccount: 'account/getGeneralLedgerList',
      getGroupMembers: 'account/getGroupMembersByGroupId',
      generateMandateRuleAction: 'account/generateMandateRuleAction',
      getAccountSignatories: 'account/getAccountSignatoriesAction',
      getInterests: 'account/getInterestList',
      getInterest: 'account/getInterestById',
      createFdAccount: 'account/createFdAccountAction',
      updateFdAccount: 'account/updateFdAccountAction',
      getFdAccounts: 'account/getFdAccountsById',
      getBranch: 'operations/getBranchList',
      getCustomerInformationByCustomerID: 'account/getCustomerInformationByCustomerID',
      getComputedInterest: 'account/getComputeInterestAction',
    }),
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    showSearchField() {
      this.showSearchButton = false
      this.resetSearchParams()
      this.resetFormData()
    },
    setInterestPayoutFrequency(value) {
      if (value === true) {
        this.isInterestPaymentFrequency = true
      } else {
        this.isInterestPaymentFrequency = false
        this.fixedDepositFormData.interestPayoutFrequency = ''
      }
    },
    getPermission() {
      if (this.actionType === 'save' || this.actionType === 'add') {
        this.permission = accountPermissions.CREATE_FIXED_DEPOSIT_ACCOUNT
        return
      }
      if (this.actionType === 'edit') {
        this.permission = accountPermissions.MODIFY_FIXED_DEPOSIT_ACCOUNT
        return
      }
      this.permission = accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT
    },
    handleSearch() {
      try {
        this.$refs.searchRules.validate().then(async success => {
          if (success) {
            const isSearchParamsNotEmpty = Object.values(
              this.searchParams,
            ).every(params => params === null || params === '')
            if (!isSearchParamsNotEmpty) {
              this.showFormErrorAlert = false
              this.showSpinner = true
              const hasFetchedCustomer = await this.fetchCustomer()
              this.showSpinner = false
              if (hasFetchedCustomer) {
                this.$root.$emit('bv::show::modal', 'search-modal')
              }
            } else {
              this.showFormErrorAlert = true
            }
          }
        })
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    async handleSubmit() {
      const action = `${this.actionType === 'add' ? this.$t('Save') : this.$t('Modify')}`
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('this Fixed Deposit Account?')
      this.confirm(this.saveFdAccountAction, { title: '', text: `${text1} ${action} ${text2}` })
    },
    async saveFdAccountAction() {
      const action = `${this.actionType === 'add' ? this.$t('Save') : this.$t('Modify')}`
      try {
        this.isLoading = true
        this.isDisabled = true
        let signatories
        if (this.signatoryList.length) {
          signatories = this.selectedSignatories.map(item => ({
            signatoryCustomerID: item.id,
            category: item.category,
          }))
        }

        const formData = {
          ...this.fixedDepositFormData,
          signatories: {
            data: signatories,
          },
          mandateRuleConfig: { ruleConfig: transformMandateRuleConfig(this.mandateList) },
          accountNumber: this.fixedDepositFormData.accountNumber.toString(),
          accountName: this.fixedDepositFormData.accountName,
          name: this.customer.fdAccountName,
          customerID: this.customer.customerID,
          liquidationAccountType: this.fixedDepositFormData.liquidationAccountType,
          liquidationAccount: this.fixedDepositFormData.accountNumber.toString(),
          productCode: this.fixedDepositFormData.productCode,
          productName: this.fixedDepositFormData.productName,
          branchCode: this.fixedDepositFormData.branchCode,
          branchName: this.fixedDepositFormData.branchName,
          enableEmailNotification: this.customers.enableEmailNotification,
          enableSMSNotification: this.customers.enableSMSNotification,
          applicableInterestCode: this.fixedDepositFormData.interestCode,
          accountCreationChannel: this.fixedDepositFormData.accountCreationChannel,
          principalAmount: toKobo(this.fixedDepositFormData.principalAmount),
          tenure: this.fixedDepositFormData.duration,
          commencementDate: toYearWithDate(this.fixedDepositFormData.commencementDate),
          applicableInterestRate: this.fixedDepositFormDatas.interestRate,
          partLiquidationCharge: this.fixedDepositFormData.earlyLiquidationCharge,
          holdFundAtMaturity: this.customers.withholdFundAtMaturity,
          applyRollover: this.customers.applyRollover,
          rolloverOption: this.customers.rolloverType,
          initiatedBy: this.userDetails.email,
        }
        const response = this.actionType === 'add'
          ? await this.createFdAccount({ formData, permission: accountPermissions.CREATE_FIXED_DEPOSIT_ACCOUNT })
          : await this.updateFdAccount({ formData, permission: accountPermissions.MODIFY_FIXED_DEPOSIT_ACCOUNT })

        const { status } = await response
        if (status === 200) {
          const text1 = this.$t('Your request to')
          const text2 = this.$t('Fixed Deposit Account was successful')
          this.alertSuccess({ text: `${text1} ${action} ${text2}` })
          this.$router.push({ name: 'fixed-deposit-list' })
        }
      } catch (error) {
        this.isLoading = false
        this.isDisabled = false
        const text1 = this.$t('Your request to')
        const text2 = this.$t('Fixed Deposit Account failed. Reason')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${text1} ${action} ${text2}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    async fetchCustomer() {
      try {
        const response = await this.getCustomer({
          search: this.searchParams,
          page: this.currentPage,
          limit: this.perPage,
          permission: this.permission,
        })

        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.customerList = data.data.map(item => ({
            ...item,
            customerName: item.name,
            phoneNumber: item.customerType === 'Individual' ? item.phoneNumber : item.businessPhoneNumber,
          }))
          this.customerSearchPagination.totalEntries = data.recordCount
          return true
        }
        this.alertError({ text: this.$t('No record found') })
        return false
      } catch (error) {
        this.alertError({ text: error })
      }
      return false
    },
    async getLinkedCustomerAccounts() {
      try {
        const response = await this.customerAccount({
          search: { customerID: this.customer.customerID },
          page: 0,
          limit: 0,
          permission: this.permission,
        })

        const { data, status } = await response
        if (status === 200 && data.data !== null) {
          this.customerAccountOptions = data.data
            .filter(dataItem => dataItem.accountStatus === 'Active')
            .reduce(
              (acc, curr) => [
                ...acc,
                {
                  label: `${curr.customerName}/${curr.accountNumber}/${curr.productName}`,
                  value: curr.accountNumber,
                },
              ],
              [],
            )
        } else {
          this.alertError({ text: this.$t('No linked accounts found') })
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async fetchFDProducts() {
      try {
        this.isBusy = true
        const response = await this.getFDProduct({
          search: { type: 'FixedDeposit' },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productTypeOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
      return true
    },
    async getFDAccountDetails() {
      try {
        const { id, type } = this.$route.params
        if (id !== undefined) {
          this.showSpinner = true
          this.showCustomerInfo = true
          this.showSearchButton = true
          this.showBackButton = true
          this.actionType = type
          this.isDisabled = type !== 'edit'
          this.isCustomDisabled = type !== 'add'
          this.isEditable = true
          this.isReadonly = type !== 'edit'
          this.showEditable = type === 'edit'
          this.isVisible = type === 'view'
          const response = await this.getFdAccounts({ id, permission: this.permission })

          const { data, status } = response

          if (status === 200 && data.data) {
            this.fixedDepositFormDatas.interestRate = data.data.applicableInterestRate

            this.customers = {
              enableEmailNotification: data.data.enableEmailNotification,
              enableSMSNotification: data.data.enableSMSNotification,
              withholdFundAtMaturity: data.data.holdFundAtMaturity,
              applyRollover: data.data.applyRollover,
              rolloverType: data.data.rolloverOption,
              linkedNumber: data.data.liquidationAccount,
              mandateRuleConfig: {
                ruleConfig: [],
              },
            }

            this.fixedDepositFormData = {
              id: data.data.id,
              liquidationAccountType: data.data.liquidationAccountType,
              accountNumber: data.data.liquidationAccount,
              accountName: data.data.name,
              casaName: data.data.name,
              casaNumber: data.data.liquidationAccount,
              glName: data.data.name,
              glNumber: data.data.liquidationAccount,
              accountOfficerName: data.data.accountOfficerName,
              accountStatus: data.data.accountStatus,
              branchName: data.data.branchName,
              branchCode: data.data.branchCode,
              productCode: data.data.productCode,
              productName: data.data.productName,
              accountCreationChannel: data.data.accountCreationChannel,
              referenceNumber: data.data.referenceNumber,
              principalAmount: toBalance(data.data.principalAmount),
              duration: data.data.tenure,
              commencementDate: toDateWithYear(data.data.commencementDate),
              maturityDate: toDateWithYear(data.data.maturityDate),
              interestID: data.data.applicableInterestID,
              earlyLiquidationCharge: data.data.partLiquidationCharge,
              signatories: {},
              applyInterestPayoutFrequency: data.data.applyInterestPayoutFrequency,
              interestPayoutFrequency: data.data.interestPayoutFrequency,
              currencyCode: data.data.currencyCode,
            }

            if (data.data.isGroupAccount) {
              await this.fetchGroupMembers(data.data.customerID)

              const customerSignatory = await this.getAccountSignatories({
                accountNumber: data.data.liquidationAccount,
                permission: this.permission,
              })

              this.signatoryMandateList = customerSignatory.data.data.map(item => ({
                fullName: item.name,
                passport: item.passportFileUrl,
                signature: item.signatureFileUrl,
              }))
              this.totalSignatoriesCount = customerSignatory.data.data.recordCount

              this.customerType = 'Group'
              const signatoryID = customerSignatory.data.data.map(item => item.customerID)

              this.signatoryOptions = this.groupMembers.filter(item => !signatoryID.includes(item.value)).map(customer => customer.label)
              this.signatoryList = customerSignatory.data.data.map(item => ({
                signatory: item.name,
                category: item.category,
              }))
            }

            if (!data.data.isGroupAccount) {
              const customerInfo = await this.getCustomerInformationByCustomerID({ customerID: data.data.customerID, permission: this.permission })
              this.signatoryMandateList = [{
                fullName: customerInfo.data.data.name,
                passport: customerInfo.data.data.passportFileUrl,
                signature: customerInfo.data.data.signatureFileUrl,
              }]
            }

            const customerDetails = await this.getCustomerInformationByCustomerID({ customerID: data.data.customerID, permission: this.permission })
            this.customer = {
              customerID: customerDetails.data.data.customerID,
              customerName: customerDetails.data.data.name,
              fdAccountName: customerDetails.data.data.name,
            }

            if (data.data.mandateRuleIndex !== null) {
              this.mandateList = transformMandateRule(data.data.mandateRuleIndex)
              this.customers.mandateRuleConfig.ruleConfig = transformMandateRuleConfig(this.mandateList)
            }
          }
          this.showSpinner = false
        }
      } catch (error) {
        this.alertError({ error })
      } finally {
        this.showSpinner = false
      }
    },

    resetSearchParams() {
      this.searchParams = { ...null }
    },
    resetAvatarModal() {
      this.avatarLg = ''
      this.avatarModalTitle = ''
    },
    resetFormData() {
      this.customers = {
        linkedNumber: '',
        enableEmailNotification: false,
        enableSMSNotification: false,
        withholdFundAtMaturity: false,
        applyRollover: true,
        rolloverType: null,
        mandateRuleConfig: {
          ruleConfig: [],
        },
      }
      this.customer = {
        customerID: '',
        customerName: '',
        fdAccountName: '',
      }
      this.fixedDepositFormDatas.interestRate = ''
      this.fixedDepositFormData = {
        liquidationAccountType: 'SavingsAndCurrent',
        accountNumber: '',
        accountName: '',
        casaName: '',
        casaNumber: '',
        glName: '',
        glNumber: '',
        accountOfficerName: '',
        accountStatus: '',
        branchName: '',
        branchCode: '',
        productCode: '',
        productName: '',
        accountCreationChannel: '',
        principalAmount: '',
        duration: '',
        commencementDate: '',
        maturityDate: '',
        interestID: '',
        interestCode: '',
        earlyLiquidationCharge: '',
        signatories: '',
        id: '',
        referenceNumber: '',
        applyInterestPayoutFrequency: false,
        interestPayoutFrequency: '',
        currencyCode: '',
      }
      this.signatoryList = []
      this.mandateList = []
    },
    async setCustomerDetails(selectedValue) {
      try {
        this.showSpinner = true
        const response = await this.customerAccount({
          search: { accountNumber: selectedValue },
          page: 0,
          limit: 0,
          permission: this.permission,
        })

        const { data, status } = response
        const dataValue = data.data[0]
        if (status === 200 && (dataValue.accountType === 'Savings' || dataValue.accountType === 'Current')) {
          this.fixedDepositFormData = {
            ...this.fixedDepositFormData,
            liquidationAccountType: 'SavingsAndCurrent',
            accountNumber: dataValue.accountNumber,
            accountName: dataValue.accountName,
            casaName: dataValue.accountName,
            casaNumber: dataValue.accountNumber,
            accountOfficerName: dataValue.accountOfficerName,
            accountStatus: dataValue.accountStatus,
            branchName: dataValue.branchName,
            branchCode: dataValue.branchCode,
            accountCreationChannel: dataValue.accountCreationChannel,
          }
        }
        this.showSpinner = false
      } catch (error) {
        this.alertError({ text: error })
        this.showSpinner = false
      } finally {
        this.showSpinner = false
      }
    },

    async computeInterest() {
      try {
        this.$refs.fixedDepositDetailsRule.validate().then(async success => {
          if (success) {
            this.showSpinner = true
            const response = await this.getComputedInterest({
              permission: accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT,
              formData: {
                productCode: this.fixedDepositFormData.productCode,
                principalAmount: this.fixedDepositFormData.principalAmount ? toKobo(this.fixedDepositFormData.principalAmount) : 0,
                tenure: this.fixedDepositFormData.duration,
                commencementDate: this.fixedDepositFormData.commencementDate ? toYearWithDate(this.fixedDepositFormData.commencementDate) : null,
                useProductInterestRate: true,
                applicableInterestCode: this.fixedDepositFormData.interestCode,
                applicableInterestRate: this.fixedDepositFormDatas.interestRate,
                partLiquidationCharge: this.fixedDepositFormData.earlyLiquidationCharge,
              },
            })
            const { status, data } = response
            if (status === 200 && data) {
              this.computeInterestData = {
                ...data,
                effectiveDate: data.effectiveDate ? toDateWithYear(data.effectiveDate) : '',
                maturityDate: data.maturityDate ? toDateWithYear(data.maturityDate) : '',
                investmentAmount: data.investmentAmount ? toBalance(data.investmentAmount) : '',
                grossInterestAmount: data.grossInterestAmount ? toBalance(data.grossInterestAmount) : '',
                netPayout: data.netPayout ? toBalance(data.netPayout) : '',
                liquidationAccount: this.fixedDepositFormData.casaNumber,
              }
              this.$root.$emit('bv::show::modal', 'info-investment-modal')
            }
            this.showSpinner = false
            this.showComputedInterest = true
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onSearchGlAccount() {
      try {
        this.showSpinner = true
        const response = await this.glAccount({
          search: {
            code: this.fixedDepositFormData.accountNumber,
            name: this.fixedDepositFormData.accountName,
          },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { data, status } = response
        const dataValue = data.data[0]
        if (status === 200) {
          this.fixedDepositFormData = {
            liquidationAccountType: 'GeneralLedgerAccount',
            accountNumber: dataValue.code,
            accountName: dataValue.name,
            glName: dataValue.name,
            glNumber: dataValue.code,
            accountCreationChannel: dataValue.accountCreationChannel,
          }
        }
        this.showSpinner = false
      } catch (error) {
        this.alertError({ text: this.$t('This is not a GL account') })
        this.showSpinner = false
      } finally {
        this.showSpinner = false
      }
    },
    async onSelectCustomer(items) {
      this.hideModal('search-modal')
      this.showSpinner = true
      const customerInfo = { ...items[0] }
      this.customer.customerID = customerInfo.customerID
      this.customer.customerName = customerInfo.customerName
      this.customer.fdAccountName = customerInfo.customerName
      this.customers.linkedNumber = customerInfo.accountNumber
      this.customer.accountNumber = customerInfo.accountNumber
      this.customer.accountName = customerInfo.customerName
      this.customerType = customerInfo.customerType
      this.showCustomerInfo = true
      this.showSearchButton = true
      this.showBackButton = true

      if (this.customerType !== 'Individual') {
        this.showSpinner = true
        await this.fetchGroupMembers(customerInfo.customerID)
        this.showSpinner = false
      }

      if (this.customerType.toLowerCase() === 'individual') {
        this.signatoryMandateList = [{
          fullName: customerInfo.name,
          passport: customerInfo.passportFileUrl,
          signature: customerInfo.signatureFileUrl,
        }]
      }

      this.hideModal('search-modal')
      if (this.customerType === 'Group') {
        this.showSpinner = true
        await this.fetchGroupMembers(customerInfo.customerID)
        this.showSpinner = false
      }
      await this.getLinkedCustomerAccounts()
      this.showCustomerInfo = true
      this.showSearchButton = true
      this.showBackButton = true

      this.showSpinner = false
    },
    async setProductTypeValues(selectedValue) {
      try {
        this.isBusy = true
        const { id } = this.$route.params
        if (id === undefined) {
          const response = await this.getFDProduct({
            search: { type: 'FixedDeposit', code: selectedValue },
            page: 0,
            limit: 0,
            permission: this.permission,
          })
          const { status, data } = response
          if (status === 200 && data.data) {
            this.fixedDepositFormData.interestID = data.data[0].interestID
            this.fixedDepositFormDatas.interestRate = data.data[0].interestRate
            this.fixedDepositFormData.interestCode = data.data[0].interestCode
            this.fixedDepositFormData.earlyLiquidationCharge = data.data[0].partLiquidationCharge
            this.fixedDepositFormData.currencyCode = data.data[0].currencyCode
            this.fixedDepositFormData.productName = data.data[0].name

            this.interestOptions = this.interestDetails
              .filter(dataItem => dataItem.status === 'Active' && dataItem.type === 'Credit')
              .map(item => ({
                label: item.name,
                value: item.id,
              }))
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
      return true
    },

    async fetchInterest() {
      try {
        const response = await this.getInterests({
          search: '',
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.interestDetails = data.data
          this.interestOptions = data.data.filter(dataItem => dataItem.status === 'Active' && dataItem.type === 'Credit').map(item => ({ label: item.name, value: item.id }))
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    setBranchInfo(item) {
      this.fixedDepositFormData.branchName = item.label
      this.fixedDepositFormData.branchCode = item.value
    },
    async setProductCreditInterestFee(selectedValue) {
      try {
        const { id } = this.$route.params
        if (id === undefined) {
          const response = await this.getInterest({ id: selectedValue, permission: this.permission })
          const { status, data } = await response
          if (status === 200 && data.data) {
            this.fixedDepositFormDatas.interestRate = data.data.rate
            this.fixedDepositFormData.interestCode = data.data.code
          }
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    setEndDate(dateStr) {
      const selectedDate = Array.isArray(dateStr) ? dateStr[0] : dateStr
      this.fixedDepositFormData.maturityDate = getEndDate(selectedDate, this.fixedDepositFormData.duration)
    },
    setApplyRollover(selectedValue) {
      this.isApplyRollover = selectedValue
        ? selectedValue === true
        : false
      if (this.isApplyRollover === false) {
        this.customer.rolloverType = ''
      }
    },
    async fetchGroupMembers(id) {
      const { permission } = this
      const formData = {
        groupCustomerID: id,
        pageNumber: 0,
        pageSize: 0,
      }

      const response = await this.getGroupMembers({
        formData, permission,
      })
      const { data, status } = response
      if (status === 200 && data.data) {
        this.signatoryOptions = data.data?.map(item => item.name)
        this.signatories = data.data?.map(item => (item.name)) // used to hold signatories values
        this.groupMembers = data.data?.map(item => ({ label: item.name, value: item.customerID }))

        this.signatoryMandateList = data.data?.map(item => ({
          fullName: item.name,
          passport: item.passportFileUrl,
          signature: item.signatureFileUrl,
        }))
      }
    },
    async createRuleInfoAction() {
      this.$refs.ruleConfigurationRules.validate().then(async success => {
        if (success) {
          this.isAddRuleDisabled = true
          this.isAddRuleLoading = true
          const filteredCategoryConfig = this.mandate.ruleConfig[0].categoryConfig.filter(item => item.authorizableNumber !== 0)
          if (filteredCategoryConfig.length) {
            const formData = {
              ruleConfig: [
                {
                  categoryConfig: filteredCategoryConfig,
                  isLimited: this.mandate.ruleConfig[0].isLimited,
                  amount: this.mandate.ruleConfig[0].amount,
                },
              ],
            }
            const response = await this.generateMandateRuleAction({ formData, permission: accountPermissions.VIEW_FIXED_DEPOSIT_ACCOUNT })
            this.customers.mandateRuleConfig.ruleConfig.push(formData.ruleConfig[0])
            const { data, status } = response
            if (status === 200) {
              data.data.forEach(item => {
                this.mandateList.push(item)
              })
            }
          } else {
            this.alertError({ text: this.$t('Please enter a value for customer count to proceed') })
          }
        }
      }).catch(error => {
        this.alertError({ text: error })
      }).finally(() => {
        this.isAddRuleDisabled = false
        this.isAddRuleLoading = false
      })
    },
    handleAccountType(selectedValue) {
      this.isAccountType = selectedValue
        ? selectedValue === 'SavingsAndCurrent'
        : 'GeneralLedgerAccount'

      const isAccountType = selectedValue
      if (isAccountType === 'GeneralLedgerAccount') {
        this.customer.accountNumber = ''
        this.customer.accountName = ''
        this.fixedDepositFormData.glName = ''
        this.fixedDepositFormData.glNumber = ''
      } else {
        this.customers.linkedNumber = ''
        this.fixedDepositFormData.casaName = ''
        this.fixedDepositFormData.casaNumber = ''
      }
    },
    onViewSignatoryMandate() {
      this.$root.$emit('bv::show::modal', 'signatory-mandate-table')
    },
    onViewPassport(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Passport')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    onViewSignature(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Signature')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    setAmountLimit(selectedValue) {
      if (!selectedValue) {
        const index = this.mandate.ruleConfig.findIndex(item => item.isLimited === selectedValue)

        if (index > -1) {
          this.mandate.ruleConfig[index].amount = 0
          return false
        }
      }
      return true
    },
    async fetchAccountSignatories(accountNumber) {
      try {
        const response = await this.getAccountSignatories(accountNumber)
        const { data, status } = await response
        if (status === 200 && data.data) {
          this.signatoryList = data.data.map(item => ({
            ...item,
            signatory: `${item.lastName} ${item.firstName} ${item.otherNames}`,
          }))
          // this.signatoryOptions = data.data.map(item => (`${item.lastName} ${item.firstName} ${item.otherNames}`
          // ))

          // this.selectedSignatories = data.data.map(item => ({
          //   id: item.customerID,
          //   signatory: `${item.lastName} ${item.firstName} ${item.otherNames}`,
          //   category: item.category.trim(),
          // }))

          // this.groupMembers = data.data.map(item => ({ label: `${item.lastName} ${item.firstName} ${item.otherNames}`, value: item.customerID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    createSignatoryAction() {
      this.$refs.signatoryRules.validate().then(async success => {
        if (success) {
          // const hasCategory = this.mandate.ruleConfig[0].categoryConfig.find(item => item.category === this.category)

          this.groupMembers.map(item => {
            if (item.label === this.signatory) {
              this.selectedSignatories.push({ id: item.value, signatory: this.signatory, category: this.category })
              return { id: item.value, signatory: this.signatory, category: this.category }
            }
            return ''
          })
          this.signatoryList = [
            ...this.signatoryList,
            { signatory: this.signatory, category: this.category },
          ]
          this.signatoryOptions = this.signatoryOptions.filter(item => item !== this.signatory)
          if (!this.signatoryOptions.length) {
            this.signatory = ''
            this.signatoryRule = ''
          }
          this.mandate.ruleConfig[0].categoryConfig.push(
            {
              category: this.category,
              authorizableNumber: 0,
            },
          )
        }
      })
    },
    removeSignatoryAction(items) {
      const removeSignatory = () => {
        const selectedSignatoryIndex = this.signatoryList.findIndex(item => item.signatory === items.signatory)
        const selectedSignatoryItem = this.signatoryList.find(item => item.signatory === items.signatory)
        const selectedMandateConfigIndex = this.mandate.ruleConfig[0].categoryConfig.findIndex(item => item.category.toLowerCase() === items.category.toLowerCase())

        this.signatoryList.splice(selectedSignatoryIndex, 1)
        this.mandate.ruleConfig[0].categoryConfig.splice(selectedMandateConfigIndex, 1)
        this.signatoryOptions.push(selectedSignatoryItem.signatory)
        this.mandateList = this.mandateList.filter(item => !item.rule.includes(items.category))
        this.selectedSignatories = this.selectedSignatories.filter(item => item.category !== items.category)

        if (!this.mandateList.length) {
          this.signatoryList = []
          this.signatoryOptions = this.signatories
        }
        this.signatoryRule = 'required'
      }
      this.confirm(removeSignatory, { title: '', text: this.$t('Are you sure you want to remove this signatory?') })
    },
    removeMandateAction(items) {
      const removeMandate = () => {
        const selectedMandateIndex = this.mandateList.findIndex(item => item.amount === items.amount)
        const selectedMandateConfigIndex = this.customers.mandateRuleConfig.ruleConfig.findIndex(item => item.amount === items.amount)
        this.mandateList.splice(selectedMandateIndex, 1)
        this.customers.mandateRuleConfig.ruleConfig.splice(selectedMandateConfigIndex, 1)
        this.signatoryList = this.signatoryList.filter(item => !items.rule.includes(item.category))
        this.selectedSignatories = this.selectedSignatories.filter(item => item.category !== items.category)

        if (!this.signatoryList.length) {
          this.mandateList = []
          this.signatoryOptions = this.signatories
        }
      }

      this.confirm(removeMandate, { title: '', text: this.$t('Are you sure you want to remove this mandate configuration?') })
    },
    backNavigationAction() {
      this.showBackButton = true
      this.$router.push({
        name: 'fixed-deposit-list',
      })
    },

    // Customer Details
    validateCustomerDetails() {
      return new Promise((resolve, reject) => {
        this.$refs.customerDetailsRule.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    // Liquidation Acccount Information
    validateLiquidationAccountInfo() {
      return new Promise((resolve, reject) => {
        this.$refs.liquidationAccountRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    // Mandate Information
    validateMandateInformation() {
      return new Promise((resolve, reject) => {
        if (this.customerType === 'Group' && !this.isVisible && !this.showEditable) {
          this.$refs.signatoryRules.validate().then(success => {
            if (success) {
              resolve(true)
            } else {
              reject()
            }
          })
        } else {
          resolve(true)
        }
      })
    },
    // Fixed Deposit Info
    validateFixedDepositInfo() {
      return new Promise((resolve, reject) => {
        this.$refs.fixedDepositDetailsRule.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
